import React, { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'utils/i18n';
import { ARCHITECTURE_PAGES, getCurrentArchitecturePage, getChildrenOfPage, getParentPage } from './architecturePagesConfig';

/**
 * ArchitectureNavigation - Vertical tab navigation for architecture pages
 * Displays as a vertical list in the left sidebar (desktop) or dropdown (mobile)
 * Shows sub-pages on hover for pages with children
 * When on a sub-page, shows sibling pages and a back button
 * Props:
 * - mobileOnly: if true, only show mobile dropdown; if false, only show desktop navigation
 */
const ArchitectureNavigation = ({ mobileOnly = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [hoveredPageId, setHoveredPageId] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
  const buttonRefs = useRef({});
  const hideTimeoutRef = useRef(null);
  const submenuRef = useRef(null);
  const portalContainerRef = useRef(null);

  // Clear hover state when navigating to a new page
  useEffect(() => {
    setHoveredPageId(null);
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, [location.pathname]);

  // Get current architecture page
  const currentPage = useMemo(() => {
    return getCurrentArchitecturePage(location.pathname);
  }, [location.pathname]);

  // Check if we're on a sub-page (child page)
  const isOnSubPage = useMemo(() => {
    if (!currentPage) return false;
    const parent = getParentPage(currentPage.id);
    return parent !== null;
  }, [currentPage]);

  // Get parent page if we're on a sub-page
  const parentPage = useMemo(() => {
    if (!currentPage) return null;
    return getParentPage(currentPage.id);
  }, [currentPage]);

  // Get sibling pages (children of parent) if we're on a sub-page
  const siblingPages = useMemo(() => {
    if (!isOnSubPage || !parentPage) return [];
    return getChildrenOfPage(parentPage.id);
  }, [isOnSubPage, parentPage]);

  // Get language prefix from current path
  const langPrefix = useMemo(() => {
    const match = location.pathname.match(/^\/([a-z]{2})\//);
    return match ? `/${match[1]}` : '';
  }, [location.pathname]);

  // Get sidebar right edge position for submenu placement
  const getSidebarRightEdge = () => {
    const sidebar = document.querySelector('.docs-layout aside, .legal-layout aside, .document-layout aside');
    if (sidebar) {
      const rect = sidebar.getBoundingClientRect();
      return rect.right;
    }
    // Fallback: sidebar width (280px)
    return 280;
  };

  // Navigate to architecture page
  const handleTabClick = (page, event) => {
    // Prevent event propagation to ensure click always works
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Clear hover state before navigating
    setHoveredPageId(null);
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    const targetPath = langPrefix ? `${langPrefix}${page.path}` : page.path;
    navigate(targetPath);
  };

  // Handle back to parent
  const handleBackToParent = () => {
    if (parentPage) {
      handleTabClick(parentPage);
    }
  };

  // Check if a page is active
  const isActive = (page) => {
    return currentPage && currentPage.id === page.id;
  };

  // Get translated label
  const getLabel = (page) => {
    if (!page) return '';
    
    // Try to get translation using the translation key
    const translationKey = `architecture.${page.translationKey}`;
    const translated = t(translationKey);
    
    // If translation exists and is different from the key, use it
    if (translated && translated !== translationKey) {
      return translated;
    }
    
    // Fallback to page.label if it exists and is a readable string
    if (page.label && typeof page.label === 'string' && !page.label.includes('.')) {
      return page.label;
    }
    
    // Last resort: try to extract a readable name from the translation key or label
    if (page.translationKey) {
      const parts = page.translationKey.split('.');
      const lastPart = parts[parts.length - 1];
      // Convert kebab-case or snake_case to Title Case
      const readable = lastPart
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
      return readable;
    }
    
    return page.label || translationKey;
  };

  // Handle mouse enter on page button
  const handleMouseEnter = (pageId, event) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const children = getChildrenOfPage(pageId);
    if (children.length === 0) {
      return;
    }

    const button = buttonRefs.current[pageId] || event.currentTarget;
    const rect = button.getBoundingClientRect();
    const sidebarRightEdge = getSidebarRightEdge();

    setHoveredPageId(pageId);
    setSubmenuPosition({
      top: rect.top,
      left: sidebarRightEdge
    });
  };

  // Handle mouse leave with delay
  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setHoveredPageId(null);
    }, 150);
  };

  // Create stable portal container
  useEffect(() => {
    if (!portalContainerRef.current) {
      portalContainerRef.current = document.createElement('div');
      portalContainerRef.current.id = 'architecture-submenu-portal';
      document.body.appendChild(portalContainerRef.current);
    }
    return () => {
      // Cleanup portal container on unmount
      if (portalContainerRef.current && portalContainerRef.current.parentNode) {
        try {
          portalContainerRef.current.parentNode.removeChild(portalContainerRef.current);
        } catch (e) {
          // Ignore errors if already removed
          console.warn('Portal container cleanup warning:', e);
        }
        portalContainerRef.current = null;
      }
      // Cleanup timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Render submenu
  const renderSubmenu = () => {
    if (!hoveredPageId) return null;

    const children = getChildrenOfPage(hoveredPageId);
    if (children.length === 0) return null;

    const submenuStyle = {
      position: 'fixed',
      top: `${submenuPosition.top}px`,
      left: `${submenuPosition.left}px`,
      zIndex: 99999,
      backgroundColor: 'var(--color-bg-primary)',
      border: '1px solid var(--color-border-light)',
      borderRadius: 'var(--radius-md)',
      boxShadow: 'var(--shadow-lg)',
      padding: 'var(--spacing-xs)',
      minWidth: '200px',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--spacing-xs)'
    };

    const submenuItemStyle = {
      padding: 'var(--spacing-sm) var(--spacing-md)',
      fontSize: 'var(--text-sm)',
      color: 'var(--color-text-primary)',
      textDecoration: 'none',
      borderRadius: 'var(--radius-sm)',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      border: 'none',
      background: 'transparent',
      textAlign: 'left',
      width: '100%'
    };

    // Invisible bridge to maintain hover state
    // Bridge only covers the gap between button and submenu, not the button itself
    const button = buttonRefs.current[hoveredPageId];
    const bridgeStyle = button ? {
      position: 'fixed',
      top: `${submenuPosition.top}px`,
      left: `${button.getBoundingClientRect().right}px`, // Start AFTER the button, not at its left edge
      width: `${submenuPosition.left - button.getBoundingClientRect().right}px`, // Only the gap width
      height: `${button.getBoundingClientRect().height}px`,
      zIndex: 99998,
      pointerEvents: 'auto' // Need pointer events to detect hover in the gap
    } : { display: 'none' };

    // Only render portal if container exists
    if (!portalContainerRef.current) return null;

    return createPortal(
      <>
        <div
          style={bridgeStyle}
          onMouseEnter={() => {
            if (hideTimeoutRef.current) {
              clearTimeout(hideTimeoutRef.current);
              hideTimeoutRef.current = null;
            }
          }}
          onMouseLeave={handleMouseLeave}
        />
        <div
          ref={submenuRef}
          style={submenuStyle}
          onMouseEnter={() => {
            if (hideTimeoutRef.current) {
              clearTimeout(hideTimeoutRef.current);
              hideTimeoutRef.current = null;
            }
          }}
          onMouseLeave={handleMouseLeave}
        >
          {children.map((child) => {
            const childActive = currentPage && (currentPage.id === child.id || location.pathname.startsWith(child.path));
            return (
              <button
                key={child.id}
                onClick={() => handleTabClick(child)}
                style={{
                  ...submenuItemStyle,
                  backgroundColor: childActive ? 'var(--color-primary-light)' : 'transparent',
                  color: childActive ? 'var(--color-primary)' : 'var(--color-text-primary)',
                  fontWeight: childActive ? '600' : '400'
                }}
                onMouseEnter={(e) => {
                  if (!childActive) {
                    e.target.style.backgroundColor = 'var(--color-bg-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!childActive) {
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {getLabel(child)}
              </button>
            );
          })}
        </div>
      </>,
      portalContainerRef.current
    );
  };

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xs)',
    marginBottom: 'var(--spacing-lg)',
    paddingBottom: 'var(--spacing-lg)',
    borderBottom: '1px solid var(--color-border-light)'
  };

  const getTabStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    fontSize: 'var(--text-sm)',
    fontWeight: active ? '600' : '500',
    color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
    textDecoration: 'none',
    borderRadius: 'var(--radius-md)',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    border: 'none',
    background: active ? 'var(--color-primary-light)' : 'transparent',
    textAlign: 'left',
    width: '100%',
    position: 'relative',
    justifyContent: 'space-between',
    ...(active ? { borderLeft: '3px solid var(--color-primary)' } : {})
  });

  const backButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    fontSize: 'var(--text-sm)',
    fontWeight: '500',
    color: 'var(--color-text-secondary)',
    backgroundColor: 'var(--color-bg-hover)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    marginBottom: 'var(--spacing-sm)',
    gap: 'var(--spacing-xs)',
    width: '100%',
    textAlign: 'left'
  };

  // Mobile dropdown handler
  const handleMobileSelect = (e) => {
    const selectedPage = ARCHITECTURE_PAGES.find(p => p.id === e.target.value);
    if (selectedPage) {
      handleTabClick(selectedPage);
    }
  };

  // If mobileOnly is explicitly set, show only that version
  if (mobileOnly === true) {
    return (
      <div className="docs-navigation-mobile">
        <select
          value={currentPage?.id || ''}
          onChange={handleMobileSelect}
          aria-label="Select architecture page"
          className="docs-nav-dropdown"
        >
          {ARCHITECTURE_PAGES.map((page) => (
            <option key={page.id} value={page.id}>
              {getLabel(page)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Render navigation - show siblings if on sub-page, show children if on parent page, otherwise show top-level pages
  const renderNavigation = () => {
    // If we're on a sub-page, show back button and sibling pages
    if (isOnSubPage && parentPage && siblingPages.length > 0) {
      return (
        <>
          {/* Back button */}
          <button
            onClick={handleBackToParent}
            style={backButtonStyle}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-bg-tertiary)';
              e.target.style.color = 'var(--color-text-primary)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-bg-hover)';
              e.target.style.color = 'var(--color-text-secondary)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
            <span>{getLabel(parentPage)}</span>
          </button>

          {/* Sibling pages */}
          {siblingPages.map((sibling) => {
            const active = isActive(sibling);
            const siblingHasChildren = getChildrenOfPage(sibling.id).length > 0;
            return (
              <div key={sibling.id} style={{ position: 'relative' }}>
                <button
                  ref={(el) => {
                    if (el) buttonRefs.current[sibling.id] = el;
                  }}
                  onClick={() => handleTabClick(sibling)}
                  aria-current={active ? 'page' : undefined}
                  className={`docs-nav-tab-vertical ${active ? 'active' : ''}`}
                  style={getTabStyle(active)}
                  onMouseEnter={(e) => {
                    if (siblingHasChildren) {
                      handleMouseEnter(sibling.id, e);
                    }
                    if (!active) {
                      e.target.style.backgroundColor = 'var(--color-bg-hover)';
                      e.target.style.color = 'var(--color-text-primary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (siblingHasChildren) {
                      handleMouseLeave();
                    }
                    if (!active) {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = 'var(--color-text-secondary)';
                    }
                  }}
                >
                  <span>{getLabel(sibling)}</span>
                  {siblingHasChildren && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 'auto' }}>
                      <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                    </svg>
                  )}
                </button>
              </div>
            );
          })}
        </>
      );
    }

    // If we're on a top-level page with children, show the page and its children directly
    if (currentPage && !isOnSubPage) {
      const currentPageChildren = getChildrenOfPage(currentPage.id);
      if (currentPageChildren.length > 0) {
        return (
          <>
            {/* Current page (highlighted) */}
            <div style={{
              ...getTabStyle(true),
              cursor: 'default',
              marginBottom: 'var(--spacing-xs)'
            }}>
              <span>{getLabel(currentPage)}</span>
            </div>

            {/* Children pages */}
            {currentPageChildren.map((child) => {
              const active = isActive(child);
              const childHasChildren = getChildrenOfPage(child.id).length > 0;
              return (
                <div key={child.id} style={{ position: 'relative', marginLeft: 'var(--spacing-md)' }}>
                  <button
                    ref={(el) => {
                      if (el) buttonRefs.current[child.id] = el;
                    }}
                    onClick={() => handleTabClick(child)}
                    aria-current={active ? 'page' : undefined}
                    className={`docs-nav-tab-vertical ${active ? 'active' : ''}`}
                    style={getTabStyle(active)}
                    onMouseEnter={(e) => {
                      if (childHasChildren) {
                        handleMouseEnter(child.id, e);
                      }
                      if (!active) {
                        e.target.style.backgroundColor = 'var(--color-bg-hover)';
                        e.target.style.color = 'var(--color-text-primary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (childHasChildren) {
                        handleMouseLeave();
                      }
                      if (!active) {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = 'var(--color-text-secondary)';
                      }
                    }}
                  >
                    <span>{getLabel(child)}</span>
                    {childHasChildren && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 'auto' }}>
                        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                      </svg>
                    )}
                  </button>
                </div>
              );
            })}
          </>
        );
      }
    }

    // Otherwise, show top-level pages (children appear on hover via submenu when not on that page)
    return ARCHITECTURE_PAGES.map((page) => {
      const active = isActive(page);
      const hasChildren = getChildrenOfPage(page.id).length > 0;
      return (
        <div key={page.id} style={{ position: 'relative' }}>
          <button
            ref={(el) => {
              if (el) buttonRefs.current[page.id] = el;
            }}
            onClick={(e) => handleTabClick(page, e)}
            aria-current={active ? 'page' : undefined}
            className={`docs-nav-tab-vertical ${active ? 'active' : ''}`}
            style={getTabStyle(active)}
            onMouseEnter={(e) => {
              // Only show hover submenu if not currently on this page
              if (hasChildren && !active) {
                handleMouseEnter(page.id, e);
              }
              if (!active) {
                e.target.style.backgroundColor = 'var(--color-bg-hover)';
                e.target.style.color = 'var(--color-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (hasChildren && !active) {
                handleMouseLeave();
              }
              if (!active) {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = 'var(--color-text-secondary)';
              }
            }}
          >
            <span>{getLabel(page)}</span>
            {hasChildren && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 'auto' }}>
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            )}
          </button>
        </div>
      );
    });
  };

  if (mobileOnly === false) {
    return (
      <>
        <nav 
          className="docs-navigation-vertical docs-navigation-desktop" 
          aria-label="Architecture pages navigation"
          style={containerStyle}
        >
          {renderNavigation()}
        </nav>
        {renderSubmenu()}
      </>
    );
  }

  // Default: Show both, CSS will handle visibility
  return (
    <>
      {/* Desktop: Vertical tab navigation */}
      <nav 
        className="docs-navigation-vertical docs-navigation-desktop" 
        aria-label="Architecture pages navigation"
        style={containerStyle}
      >
        {renderNavigation()}
      </nav>
      {renderSubmenu()}

      {/* Mobile: Dropdown select */}
      <div className="docs-navigation-mobile">
        <select
          value={currentPage?.id || ''}
          onChange={handleMobileSelect}
          aria-label="Select architecture page"
          className="docs-nav-dropdown"
        >
          {ARCHITECTURE_PAGES.map((page) => (
            <option key={page.id} value={page.id}>
              {getLabel(page)}
            </option>
          ))}
        </select>
      </div>
    </>
  );
};

export default ArchitectureNavigation;
